import { dirname } from "path";

import resolve from "resolve/async";
import { resolveSymlink } from "./fs";
import { getPackageInfo, getPackageName } from "./util";
import { promisify } from "util";
import resolvePackageExports from "./package/resolvePackageExports";
import resolvePackageImports from "./package/resolvePackageImports";
import { findPackageJson, ResolveError } from "./package/utils";
import { createResolveFsOptions } from "../resolver/resolve";

const resolveImportPath = promisify(resolve);

async function getPackageJson(
  importer,
  pkgName,
  resolveOptions,
  moduleDirectories,
  fs,
) {
  if (importer) {
    const selfPackageJsonResult = await findPackageJson(
      importer,
      moduleDirectories,
      fs,
    );
    if (
      selfPackageJsonResult &&
      selfPackageJsonResult.pkgJson.name === pkgName
    ) {
      // the referenced package name is the current package
      return selfPackageJsonResult;
    }
  }

  try {
    const pkgJsonPath = await resolveImportPath(
      `${pkgName}/package.json`,
      resolveOptions,
    );
    const pkgJson = JSON.parse(await fs.readFile(pkgJsonPath, "utf8"));
    return { pkgJsonPath, pkgJson, pkgPath: dirname(pkgJsonPath) };
  } catch (_) {
    return null;
  }
}

async function resolveIdClassic({
  importSpecifier,
  packageInfoCache,
  extensions,
  mainFields,
  preserveSymlinks,
  useBrowserOverrides,
  baseDir,
  moduleDirectories,
  modulePaths,
  rootDir,
  ignoreSideEffectsForRoot,
  fs,
}) {
  let hasModuleSideEffects = () => null;
  let hasPackageEntry = true;
  let packageBrowserField = false;
  let packageInfo;

  const filter = (pkg, pkgPath) => {
    const info = getPackageInfo({
      cache: packageInfoCache,
      extensions,
      pkg,
      pkgPath,
      mainFields,
      preserveSymlinks,
      useBrowserOverrides,
      rootDir,
      ignoreSideEffectsForRoot,
      fs,
    });

    ({
      packageInfo,
      hasModuleSideEffects,
      hasPackageEntry,
      packageBrowserField,
    } = info);

    return info.cachedPkg;
  };

  const resolveOptions = {
    ...createResolveFsOptions(fs),
    basedir: await fs.realpath(baseDir),
    extensions,
    includeCoreModules: false,
    moduleDirectory: moduleDirectories,
    paths: modulePaths,
    preserveSymlinks,
    packageFilter: filter,
  };

  let location;
  try {
    location = await resolveImportPath(importSpecifier, resolveOptions);
  } catch (error) {
    if (error.code !== "MODULE_NOT_FOUND") {
      throw error;
    }
    return null;
  }

  return {
    location: preserveSymlinks ? location : await resolveSymlink(location, fs),
    hasModuleSideEffects,
    hasPackageEntry,
    packageBrowserField,
    packageInfo,
  };
}

async function resolveWithExportMap({
  importer,
  importSpecifier,
  exportConditions,
  packageInfoCache,
  extensions,
  mainFields,
  preserveSymlinks,
  useBrowserOverrides,
  baseDir,
  moduleDirectories,
  modulePaths,
  rootDir,
  ignoreSideEffectsForRoot,
  allowExportsFolderMapping,
  fs,
  validateImportsExports,
}) {
  if (importSpecifier.startsWith("#")) {
    // this is a package internal import, resolve using package imports field
    const resolveResult = await resolvePackageImports({
      importSpecifier,
      importer,
      moduleDirs: moduleDirectories,
      conditions: exportConditions,
      validateImportsExports,
      fs,
      resolveId(id /* , parent*/) {
        return resolveIdClassic({
          importSpecifier: id,
          packageInfoCache,
          extensions,
          mainFields,
          preserveSymlinks,
          useBrowserOverrides,
          baseDir,
          moduleDirectories,
          modulePaths,
          fs,
        });
      },
    });

    const location = resolveResult;
    return {
      location,
      location: preserveSymlinks
        ? location
        : await resolveSymlink(location, fs),
      hasModuleSideEffects: () => null,
      hasPackageEntry: true,
      packageBrowserField: false,
      // eslint-disable-next-line no-undefined
      packageInfo: undefined,
    };
  }

  const pkgName = getPackageName(importSpecifier);
  if (pkgName) {
    // it's a bare import, find the package.json and resolve using package exports if available
    let hasModuleSideEffects = () => null;
    let hasPackageEntry = true;
    let packageBrowserField = false;
    let packageInfo;

    const filter = (pkg, pkgPath) => {
      const info = getPackageInfo({
        cache: packageInfoCache,
        extensions,
        pkg,
        pkgPath,
        mainFields,
        preserveSymlinks,
        useBrowserOverrides,
        rootDir,
        ignoreSideEffectsForRoot,
        fs,
      });

      ({
        packageInfo,
        hasModuleSideEffects,
        hasPackageEntry,
        packageBrowserField,
      } = info);

      return info.cachedPkg;
    };

    const resolveOptions = {
      ...createResolveFsOptions(fs),
      basedir: await fs.realpath(baseDir),
      extensions,
      includeCoreModules: false,
      moduleDirectory: moduleDirectories,
      paths: modulePaths,
      preserveSymlinks,
      packageFilter: filter,
    };

    const result = await getPackageJson(
      importer,
      pkgName,
      resolveOptions,
      moduleDirectories,
      fs,
    );

    if (result && result.pkgJson.exports) {
      const { pkgJson, pkgJsonPath } = result;
      const subpath =
        pkgName === importSpecifier
          ? "."
          : `.${importSpecifier.substring(pkgName.length)}`;
      const pkgDr = pkgJsonPath.replace("package.json", "");
      const pkgURL = pkgDr;

      const context = {
        importer,
        importSpecifier,
        moduleDirs: moduleDirectories,
        pkgURL,
        pkgJsonPath,
        allowExportsFolderMapping,
        conditions: exportConditions,
      };
      const resolvedPackageExport = await resolvePackageExports(
        context,
        subpath,
        pkgJson.exports,
        validateImportsExports,
      );
      const location = resolvedPackageExport;
      if (location) {
        return {
          location: preserveSymlinks
            ? location
            : await resolveSymlink(location, fs),
          hasModuleSideEffects,
          hasPackageEntry,
          packageBrowserField,
          packageInfo,
        };
      }
    }
  }

  return null;
}

async function resolveWithClassic({
  importer,
  importSpecifierList,
  exportConditions,
  warn,
  packageInfoCache,
  extensions,
  mainFields,
  preserveSymlinks,
  useBrowserOverrides,
  baseDir,
  moduleDirectories,
  modulePaths,
  rootDir,
  ignoreSideEffectsForRoot,
  fs,
}) {
  for (let i = 0; i < importSpecifierList.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const result = await resolveIdClassic({
      importer,
      importSpecifier: importSpecifierList[i],
      exportConditions,
      warn,
      packageInfoCache,
      extensions,
      mainFields,
      preserveSymlinks,
      useBrowserOverrides,
      baseDir,
      moduleDirectories,
      modulePaths,
      rootDir,
      ignoreSideEffectsForRoot,
      fs,
    });

    if (result) {
      return result;
    }
  }

  return null;
}

// Resolves to the module if found or `null`.
// The first import specifier will first be attempted with the exports algorithm.
// If this is unsuccessful because export maps are not being used, then all of `importSpecifierList`
// will be tried with the classic resolution algorithm
export default async function resolveImportSpecifiers({
  importer,
  importSpecifierList,
  exportConditions,
  warn,
  packageInfoCache,
  extensions,
  mainFields,
  preserveSymlinks,
  useBrowserOverrides,
  baseDir,
  moduleDirectories,
  modulePaths,
  rootDir,
  ignoreSideEffectsForRoot,
  allowExportsFolderMapping,
  fs,
  validateImportsExports,
  ignoreExports,
}) {
  if (!ignoreExports) {
    try {
      const exportMapRes = await resolveWithExportMap({
        importer,
        importSpecifier: importSpecifierList[0],
        exportConditions,
        packageInfoCache,
        extensions,
        mainFields,
        preserveSymlinks,
        useBrowserOverrides,
        baseDir,
        moduleDirectories,
        modulePaths,
        rootDir,
        ignoreSideEffectsForRoot,
        allowExportsFolderMapping,
        fs,
        validateImportsExports,
      });
      if (exportMapRes) return exportMapRes;
    } catch (error) {
      if (error instanceof ResolveError) {
        warn(error);
        return null;
      }
      throw error;
    }
  }

  // package has no imports or exports, use classic node resolve
  return resolveWithClassic({
    importer,
    importSpecifierList,
    exportConditions,
    warn,
    packageInfoCache,
    extensions,
    mainFields,
    preserveSymlinks,
    useBrowserOverrides,
    baseDir,
    moduleDirectories,
    modulePaths,
    rootDir,
    ignoreSideEffectsForRoot,
    fs,
  });
}
